import React, { FormEvent, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  FiActivity,
  FiAlertCircle,
  FiAlertTriangle,
  FiArrowRight,
  FiCalendar,
  FiCheck,
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiDownload,
  FiFileText,
  FiFilter,
  FiHome,
  FiInfo,
  FiLock,
  FiMapPin,
  FiSearch,
  FiShield,
  FiUser,
  FiUsers,
  FiX,
} from 'react-icons/fi';
import type { IconType } from 'react-icons';

import { Button } from '../../ComponnentsUI/common/Button';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { useOrganization } from '../../../helpers/api/organization/organization';
import {
  exportAuditEvents,
  getAuditEvent,
  getAuditEvents,
  type AuditEvent,
  type AuditEventFilters,
  type AuditFieldChange,
  type AuditOutcome,
} from '../../../helpers/api/audit-events/audit-events';

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const ACTION_GROUPS = [
  ['Access & security', [
    ['auth.login', 'Sign in'],
    ['auth.sso_login', 'Single sign-on'],
    ['auth.logout', 'Sign out'],
    ['auth.2fa_enable', 'Enable two-factor authentication'],
    ['auth.2fa_disable', 'Disable two-factor authentication'],
    ['auth.password_change', 'Change password'],
    ['auth.password_reset_request', 'Request password reset'],
    ['auth.account_verify', 'Verify account'],
  ]],
  ['Users & roles', [
    ['user.create', 'Create user account'],
    ['user.update', 'Update user account'],
    ['user.activate', 'Activate user account'],
    ['user.deactivate', 'Deactivate user account'],
    ['user.delete', 'Delete user account'],
    ['role_permission.update', 'Change role permissions'],
    ['user_role_assignment.create', 'Assign role'],
    ['user_role_assignment.delete', 'Remove role assignment'],
  ]],
  ['Organization & locations', [
    ['organization.update', 'Update organization'],
    ['organization.owner_transfer', 'Transfer organization ownership'],
    ['org_unit.create', 'Create location'],
    ['org_unit.update', 'Update location'],
    ['org_unit.delete', 'Delete location'],
    ['org_unit.apply_template', 'Apply organization template'],
  ]],
  ['Patients', [
    ['patient.create', 'Create patient'],
    ['patient.create_with_pregnancy', 'Create patient with pregnancy'],
    ['patient.update', 'Update patient'],
    ['patient.archive', 'Archive patient'],
    ['patient.unarchive', 'Unarchive patient'],
    ['pregnancy.create', 'Create pregnancy'],
    ['pregnancy.update', 'Update pregnancy'],
    ['family_history.update', 'Update family history'],
    ['family_history.delete', 'Delete family history'],
    ['prior_delivery.create', 'Create prior delivery'],
  ]],
  ['Studies', [
    ['study.create', 'Create study'],
    ['study.update', 'Update study'],
    ['study.archive', 'Archive study'],
    ['study.unarchive', 'Unarchive study'],
    ['report.update', 'Update report'],
    ['frame.view_assign', 'Assign frame view'],
    ['frame.segmentation_save', 'Edit annotations'],
    ['frame.quality_flag', 'Mark structure not visible'],
  ]],
  ['Scheduling', [
    ['appointment.create', 'Create appointment'],
    ['appointment.cancel', 'Cancel appointment'],
  ]],
] as const;

const ACTIONS = ACTION_GROUPS.flatMap(([, actions]) => actions);
const ACTION_LABELS = Object.fromEntries(ACTIONS) as Record<string, string>;

// A leading glyph per activity family lets the eye group a dense log by the
// kind of thing that happened before reading a single label.
const CATEGORY_ICONS: Array<[(action: string) => boolean, IconType]> = [
  [a => a.startsWith('auth.'), FiLock],
  [a => a.startsWith('user.') || a.startsWith('role') || a.startsWith('user_role'), FiUsers],
  [a => a.startsWith('organization.') || a.startsWith('org_unit.'), FiHome],
  [a => a.startsWith('patient.') || a.startsWith('pregnancy.') || a.startsWith('family_history.') || a.startsWith('prior_delivery.'), FiUser],
  [a => a.startsWith('study.') || a.startsWith('report.') || a.startsWith('frame.'), FiFileText],
  [a => a.startsWith('appointment.'), FiCalendar],
  [a => a.startsWith('audit_log.'), FiDownload],
];

function activityIcon(action: string): IconType {
  return CATEGORY_ICONS.find(([test]) => test(action))?.[1] ?? FiActivity;
}

const REASON_LABELS: Record<string, string> = {
  account_locked: 'Too many unsuccessful attempts',
  conflict: 'The request conflicted with existing information',
  invalid_credentials: 'The credentials could not be verified',
  matrix_denied: 'The person does not have the required permission',
  not_found: 'The requested item could not be found',
  scope_denied: 'The person cannot access this location or record',
  unresolved_resource: 'The requested item could not be identified',
  user_inactive: 'The user account is inactive',
  validation_failed: 'The submitted information was not valid',
};

const OUTCOME_LABELS: Record<AuditOutcome, string> = {
  succeeded: 'Completed',
  accepted: 'In progress',
  partially_succeeded: 'Partially completed',
  failed: 'Failed',
  denied: 'Blocked',
};

const OUTCOME_STYLES: Record<AuditOutcome, string> = {
  succeeded: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300',
  accepted: 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300',
  partially_succeeded: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
  failed: 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300',
  denied: 'border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-800 dark:bg-orange-950/40 dark:text-orange-300',
};

function humanize(value: string) {
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, character => character.toUpperCase());
}

function activityLabel(event: AuditEvent) {
  const label = ACTION_LABELS[event.action];
  if (!label) return humanize(event.action.replace('.', ' '));
  if (event.outcome === 'succeeded') {
    const completed: Record<string, string> = {
      'auth.login': 'Signed in',
      'auth.sso_login': 'Signed in with single sign-on',
      'auth.logout': 'Signed out',
      'auth.2fa_enable': 'Enabled two-factor authentication',
      'auth.2fa_disable': 'Disabled two-factor authentication',
      'auth.password_change': 'Changed a password',
      'auth.password_reset_request': 'Requested a password reset',
      'auth.account_verify': 'Verified an account',
      'user.create': 'Created a user account',
      'user.update': 'Updated a user account',
      'user.activate': 'Activated a user account',
      'user.deactivate': 'Deactivated a user account',
      'user.delete': 'Deleted a user account',
      'role_permission.update': 'Changed role permissions',
      'user_role_assignment.create': 'Assigned a role',
      'user_role_assignment.delete': 'Removed a role assignment',
      'organization.update': 'Updated the organization',
      'organization.owner_transfer': 'Transferred organization ownership',
      'org_unit.create': 'Created a location',
      'org_unit.update': 'Updated a location',
      'org_unit.delete': 'Deleted a location',
      'org_unit.apply_template': 'Applied an organization template',
      'patient.create': 'Created a patient',
      'patient.create_with_pregnancy': 'Created a patient with pregnancy',
      'patient.update': 'Updated a patient',
      'patient.archive': 'Archived a patient',
      'patient.unarchive': 'Unarchived a patient',
      'pregnancy.create': 'Created a pregnancy',
      'pregnancy.update': 'Updated a pregnancy',
      'family_history.update': 'Updated family history',
      'family_history.delete': 'Deleted family history',
      'prior_delivery.create': 'Created a prior delivery',
      'study.create': 'Created a study',
      'study.update': 'Updated a study',
      'study.archive': 'Archived a study',
      'study.unarchive': 'Unarchived a study',
      'report.update': 'Updated a report',
      'frame.view_assign': 'Confirmed a frame view',
      'frame.segmentation_save': 'Edited frame annotations',
      'frame.quality_flag': 'Marked a structure not visible',
      'appointment.create': 'Created an appointment',
      'appointment.cancel': 'Cancelled an appointment',
    };
    if (event.action === 'frame.view_assign' && event.context?.operation === 'cleared') {
      return "Cleared a frame's views";
    }
    if (
      event.action === 'patient.update' &&
      event.context?.sections &&
      (!event.changed_fields || event.changed_fields.length === 0)
    ) {
      return 'Updated patient history';
    }
    if (event.action === 'report.update') {
      const touched = event.context?.touched;
      if (touched === 'comment') return 'Updated report comment';
      if (touched === 'views_display_order') return 'Reordered report views';
      if (touched === 'report_views') return 'Removed a report view';
    }
    return completed[event.action] || label;
  }
  if (event.outcome === 'denied') return `${label} blocked`;
  if (event.outcome === 'partially_succeeded') return `${label} partially completed`;
  if (event.outcome === 'accepted') return `${label} started`;
  return `${label} failed`;
}

function actorDetails(event: AuditEvent) {
  if (event.actor?.display_name) {
    return {
      primary: event.actor.deleted ? 'Former user' : event.actor.display_name,
      secondary: event.actor.email || undefined,
      initials: event.actor.display_name.split(/\s+/).map(part => part[0]).join('').slice(0, 2).toUpperCase(),
    };
  }
  if (event.actor?.email || event.actor_identifier) {
    return {
      primary: event.user_id ? 'Former user' : 'Unrecognized sign-in',
      secondary: event.actor?.email || event.actor_identifier || undefined,
      initials: '?',
    };
  }
  if (event.user_id) return { primary: 'Former user', secondary: undefined, initials: '–' };
  return { primary: 'Unknown person', secondary: undefined, initials: '?' };
}

function targetLabel(event: AuditEvent) {
  if (event.action.startsWith('auth.')) return 'Account access';
  const targets: Record<string, string> = {
    user: 'User account',
    role: 'Role',
    role_permission: 'Role permissions',
    user_role_assignment: 'Role assignment',
    organization: 'Organization',
    org_unit: 'Location',
    frame: 'Image frame',
  };
  return event.entity_type ? targets[event.entity_type] || humanize(event.entity_type) : 'No affected record';
}

// Keep in sync with ORG_WIDE_ACTION_PREFIXES in backend/src/app/audit.py.
const ORG_WIDE_ACTION_PREFIXES = [
  'audit_log.', 'auth.', 'user.', 'role.', 'role_permission.',
  'user_role_assignment.', 'organization.', 'org_unit.',
];

function scopeLabel(event: AuditEvent, orgName?: string) {
  if (event.scope?.deleted) return 'Former location';
  if (event.scope?.name) return event.scope.name;
  if (event.org_unit_id) return 'Former location';
  if (ORG_WIDE_ACTION_PREFIXES.some(prefix => event.action?.startsWith(prefix))) {
    return orgName || 'Organization-wide';
  }
  return 'No location selected';
}

function ScopeLabel({ event }: { event: AuditEvent }) {
  const { data: organization } = useOrganization();
  return <>{scopeLabel(event, (organization as any)?.name)}</>;
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(value));
}

function formatTime(value: string) {
  return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', second: '2-digit' }).format(new Date(value));
}

function OutcomeBadge({ outcome }: { outcome: AuditOutcome }) {
  const Icon = outcome === 'succeeded' ? FiCheck : outcome === 'accepted' ? FiClock : FiAlertTriangle;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${OUTCOME_STYLES[outcome]}`}>
      <Icon className="h-3.5 w-3.5" />
      {OUTCOME_LABELS[outcome]}
    </span>
  );
}

// Occurrence-kind fields are structured blobs whose full before/after diff is
// noise, so no value is stored. Give the known ones a phrase that says what kind
// of change happened instead of a bare "Changed".
const OCCURRENCE_DESCRIPTIONS: Record<string, string> = {
  fetal_anatomy: 'Anatomy status recalculated',
  report_views: 'Frame-to-view assignments changed',
  report_view_removed: 'A view was removed from the report',
  views_display_order: 'View order changed',
  comment: 'Comment updated',
  report_content: 'Report content updated',
  morphology: 'Morphology updated',
  additional_findings: 'Additional findings updated',
  additional_info: 'Additional info updated',
};

function ChangeValue({ change }: { change: AuditFieldChange }) {
  if (change.change_kind === 'occurrence') {
    const description = OCCURRENCE_DESCRIPTIONS[change.field] || 'Changed';
    return <span className="text-title-page-color-300 dark:text-gray-400">{description}</span>;
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <span className="break-all rounded bg-red-50 px-1.5 py-0.5 text-red-700 line-through dark:bg-red-950/30 dark:text-red-300">{change.old_value ?? '(empty)'}</span>
      <FiArrowRight className="h-3 w-3 shrink-0 text-title-page-color-300 dark:text-gray-500" />
      <span className="break-all rounded bg-green-50 px-1.5 py-0.5 font-medium text-green-700 dark:bg-green-950/30 dark:text-green-300">{change.new_value ?? '(empty)'}</span>
    </span>
  );
}

function FieldChangesSection({ changes }: { changes: AuditFieldChange[] }) {
  // The user's own edits are shown up front; values the system recalculated as a
  // consequence (GA, EFW, percentiles) are collapsed so they don't bury the edit.
  const direct = changes.filter(change => change.impact !== 'indirect');
  const indirect = changes.filter(change => change.impact === 'indirect');
  return (
    <section>
      <h3 className="text-sm font-semibold text-title-page-color-100 dark:text-white">What changed</h3>

      {direct.length > 0 && (
        <div className="mt-2 space-y-2">
          {direct.map((change, index) => (
            <div
              key={`d-${change.entity_type}-${change.field}-${index}`}
              className="rounded-lg border border-blue-200 bg-blue-50/40 p-3 dark:border-blue-900 dark:bg-blue-950/20"
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-medium text-title-page-color-100 dark:text-gray-200">{humanize(change.field)}</span>
                <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400">Direct edit</span>
              </div>
              <div className="mt-1.5 text-sm"><ChangeValue change={change} /></div>
            </div>
          ))}
        </div>
      )}

      {indirect.length > 0 && (
        <details className="group mt-2 rounded-lg border border-gray-200 dark:border-gray-700">
          <summary className="flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-sm text-title-page-color-300 hover:text-title-page-color-100 dark:text-gray-400 dark:hover:text-white">
            <span>{indirect.length} value{indirect.length > 1 ? 's' : ''} recalculated automatically</span>
            <FiChevronRight className="h-4 w-4 shrink-0 transition-transform group-open:rotate-90" />
          </summary>
          <dl className="border-t border-gray-200 px-3 py-1.5 dark:border-gray-700">
            {indirect.map((change, index) => (
              <div
                key={`i-${change.entity_type}-${change.field}-${index}`}
                className="flex flex-col gap-0.5 py-1 sm:flex-row sm:items-baseline sm:justify-between sm:gap-3"
              >
                <dt className="shrink-0 text-xs text-title-page-color-300 dark:text-gray-400">{humanize(change.field)}</dt>
                <dd className="min-w-0 text-xs"><ChangeValue change={change} /></dd>
              </div>
            ))}
          </dl>
        </details>
      )}
    </section>
  );
}

function Inspector({ event, onClose }: { event: AuditEvent; onClose: () => void }) {
  const actor = actorDetails(event);
  const ActivityIcon = activityIcon(event.action);
  // The list row carries no resolved detail; fetch the enriched event (patient,
  // provider, exam type, ...) when the inspector opens.
  const detailQuery = useQuery({
    queryKey: ['audit-event', event.id],
    queryFn: ({ signal }) => getAuditEvent(event.id, signal),
    staleTime: 5 * 60 * 1000,
    retry: 0,
  });
  const details = detailQuery.data?.details ?? [];
  return (
    <aside className="fixed inset-0 z-50 overflow-y-auto bg-white px-5 py-6 dark:bg-gray-900 lg:sticky lg:top-4 lg:z-0 lg:max-h-[calc(100vh-7rem)] lg:rounded-xl lg:border lg:border-gray-200 lg:bg-card-color-100 lg:px-6 lg:shadow-sm lg:dark:border-gray-700 lg:dark:bg-gray-800">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-card-color-200 text-title-page-color-300 dark:bg-gray-700 dark:text-gray-300"><ActivityIcon className="h-4 w-4" /></span>
          <div className="min-w-0">
            <p className="text-sm font-medium text-blue-600 dark:text-blue-400">Activity details</p>
            <h2 className="mt-1 text-lg font-bold text-title-page-color-100 dark:text-white">{activityLabel(event)}</h2>
          </div>
        </div>
        <button type="button" onClick={onClose} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:hover:bg-gray-700 dark:hover:text-white" aria-label="Close activity details">
          <FiX className="h-5 w-5" />
        </button>
      </div>

      <div className="mt-4"><OutcomeBadge outcome={event.outcome} /></div>

      <div className="mt-6 space-y-5">
        <section>
          <h3 className="text-sm font-semibold text-title-page-color-100 dark:text-white">Who</h3>
          <div className="mt-2 flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-50 text-xs font-bold text-blue-700 dark:bg-blue-950/50 dark:text-blue-300">{actor.initials}</span>
            <div className="min-w-0">
              <p className="font-medium text-title-page-color-100 dark:text-white">{actor.primary}</p>
              {actor.secondary && <p className="truncate text-sm text-title-page-color-300 dark:text-gray-400">{actor.secondary}</p>}
            </div>
          </div>
        </section>

        {(detailQuery.isLoading || details.length > 0) && (
          <section className="rounded-lg border border-gray-200 bg-card-color-200/50 p-4 dark:border-gray-700 dark:bg-gray-900/30">
            <h3 className="text-sm font-semibold text-title-page-color-100 dark:text-white">Details</h3>
            {detailQuery.isLoading ? (
              <div className="mt-3 space-y-2.5">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="flex justify-between gap-4">
                    <div className="h-3 w-20 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
                    <div className="h-3 w-32 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
                  </div>
                ))}
              </div>
            ) : (
              <dl className="mt-3 space-y-2.5">
                {details.map(field => (
                  <div key={field.label} className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
                    <dt className="shrink-0 text-sm text-title-page-color-300 dark:text-gray-400">{field.label}</dt>
                    <dd className="min-w-0 break-words text-sm font-medium text-title-page-color-100 dark:text-gray-200 sm:text-right">{field.value}</dd>
                  </div>
                ))}
              </dl>
            )}
          </section>
        )}

        <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
          <div>
            <h3 className="text-sm font-semibold text-title-page-color-100 dark:text-white">When</h3>
            <p className="mt-1 text-sm text-title-page-color-300 dark:text-gray-300">{formatDate(event.created_at)} at {formatTime(event.created_at)}</p>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-title-page-color-100 dark:text-white">Where</h3>
            <p className="mt-1 text-sm text-title-page-color-300 dark:text-gray-300"><ScopeLabel event={event} /></p>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-title-page-color-100 dark:text-white">Affected item</h3>
            <p className="mt-1 text-sm text-title-page-color-300 dark:text-gray-300">{targetLabel(event)}</p>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-title-page-color-100 dark:text-white">Result</h3>
            <p className="mt-1 text-sm text-title-page-color-300 dark:text-gray-300">{OUTCOME_LABELS[event.outcome]}</p>
          </div>
        </section>

        {event.reason_code && (
          <section className="rounded-lg border border-orange-200 bg-orange-50 p-3 dark:border-orange-900 dark:bg-orange-950/30">
            <h3 className="text-sm font-semibold text-orange-900 dark:text-orange-200">Why this happened</h3>
            <p className="mt-1 text-sm text-orange-800 dark:text-orange-300">{REASON_LABELS[event.reason_code] || humanize(event.reason_code)}</p>
          </section>
        )}

        {/* Names-only chips are a fallback; when we captured before/after values
            the richer "What changed" section below supersedes them. */}
        {event.changed_fields?.length && !detailQuery.data?.field_changes?.length ? (
          <section>
            <h3 className="text-sm font-semibold text-title-page-color-100 dark:text-white">Information changed</h3>
            <div className="mt-2 flex flex-wrap gap-2">
              {event.changed_fields.map(field => (
                <span key={field} className="rounded-md bg-card-color-200 px-2.5 py-1 text-xs font-medium text-title-page-color-300 dark:bg-gray-700 dark:text-gray-200">{humanize(field)}</span>
              ))}
            </div>
          </section>
        ) : null}

        {detailQuery.data?.field_changes?.length ? (
          <FieldChangesSection changes={detailQuery.data.field_changes} />
        ) : null}
      </div>

      <details className="mt-7 border-t border-gray-200 pt-4 dark:border-gray-700">
        <summary className="cursor-pointer text-sm font-medium text-title-page-color-300 hover:text-title-page-color-100 dark:text-gray-400 dark:hover:text-white">Technical details</summary>
        <dl className="mt-3 space-y-3 text-xs">
          <div><dt className="text-gray-500">Activity key</dt><dd className="mt-0.5 break-all font-mono text-gray-700 dark:text-gray-300">{event.action}</dd></div>
          <div><dt className="text-gray-500">Event reference</dt><dd className="mt-0.5 break-all font-mono text-gray-700 dark:text-gray-300">{event.id}</dd></div>
          {event.entity_id && <div><dt className="text-gray-500">Affected record reference</dt><dd className="mt-0.5 break-all font-mono text-gray-700 dark:text-gray-300">{event.entity_id}</dd></div>}
          <div><dt className="text-gray-500">Exact UTC time</dt><dd className="mt-0.5 font-mono text-gray-700 dark:text-gray-300">{new Date(event.created_at).toISOString()}</dd></div>
        </dl>
      </details>
    </aside>
  );
}

export default function AuditTrailPage() {
  useDocumentTitle('DEEPECHO - Audit trail');
  const [searchParams, setSearchParams] = useSearchParams();
  const [selected, setSelected] = useState<AuditEvent | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [cursors, setCursors] = useState<string[]>(['']);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [pageSize, setPageSize] = useState(10);
  const cursor = cursors[cursors.length - 1];

  const changePageSize = (size: number) => {
    setPageSize(size);
    setCursors(['']); // resizing the page returns to the first page
  };

  const filters: AuditEventFilters = {
    search: searchParams.get('search') || undefined,
    actor: searchParams.get('actor') || undefined,
    action: searchParams.get('action') || undefined,
    outcome: (searchParams.get('outcome') as AuditOutcome | null) || undefined,
    scope: searchParams.get('scope') || undefined,
    created_from: searchParams.get('created_from') || undefined,
    created_to: searchParams.get('created_to') || undefined,
    cursor: cursor || undefined,
    limit: pageSize,
  };

  const query = useQuery({
    queryKey: ['audit-events', filters],
    queryFn: ({ signal }) => getAuditEvents(filters, signal),
    retry: 0,
  });

  const updateFilters = (changes: Record<string, string | undefined>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([key, value]) => value ? next.set(key, value) : next.delete(key));
    setCursors(['']);
    setSearchParams(next);
  };

  const applyForm = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const from = String(data.get('from') || '');
    const to = String(data.get('to') || '');
    updateFilters({
      search: String(data.get('search') || '').trim() || undefined,
      actor: String(data.get('actor') || '').trim() || undefined,
      action: String(data.get('action') || '') || undefined,
      outcome: String(data.get('outcome') || '') || undefined,
      scope: String(data.get('scope') || '').trim() || undefined,
      created_from: from ? new Date(`${from}T00:00:00`).toISOString() : undefined,
      created_to: to ? new Date(`${to}T23:59:59`).toISOString() : undefined,
    });
  };

  const setDateRange = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - Math.max(days - 1, 0));
    start.setHours(0, 0, 0, 0);
    updateFilters({ created_from: start.toISOString(), created_to: end.toISOString() });
  };

  const handleExport = async () => {
    setExportError(null);
    setExporting(true);
    try {
      await exportAuditEvents(filters);
    } catch (error) {
      setExportError(error instanceof Error ? error.message : 'The export could not be created.');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => setSelected(null), [searchParams, cursor]);

  const activeFilterCount = ['search', 'actor', 'action', 'outcome', 'scope', 'created_from', 'created_to']
    .filter(key => searchParams.has(key)).length;
  const events = query.data?.items ?? [];

  return (
    <div className="min-h-full bg-card-color-200 px-4 py-5 dark:bg-gray-900 sm:px-6 lg:px-8">
      <div className="w-full">
        <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600 text-white shadow-sm"><FiShield className="h-5 w-5" /></span>
            <div>
              <h1 className="text-xl font-bold text-title-page-color-100 dark:text-white">Audit trail</h1>
              <p className="mt-1 max-w-2xl text-sm text-title-page-color-300 dark:text-gray-400">Who did what, where, and when — every sign-in, record change, and permission update, kept as a permanent record.</p>
            </div>
          </div>
          <Button variant="outline" onClick={handleExport} disabled={exporting} className="gap-2 self-start">
            <FiDownload /> {exporting ? 'Preparing…' : 'Export results'}
          </Button>
        </header>

        {exportError && (
          <div className="mt-4 flex items-center gap-2 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800 dark:border-orange-900 dark:bg-orange-950/30 dark:text-orange-300">
            <FiInfo className="shrink-0" /> {exportError}
          </div>
        )}

        <form key={searchParams.toString()} onSubmit={applyForm} className="mt-6 rounded-xl border border-gray-200 bg-card-color-100 p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="flex flex-col gap-3 lg:flex-row">
            <label className="relative min-w-0 flex-1">
              <span className="sr-only">Search activity</span>
              <FiSearch className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input name="search" defaultValue={searchParams.get('search') || ''} placeholder="Search people, email, location, activity, or affected item" className="h-10 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-3 text-sm text-title-page-color-100 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:focus:ring-blue-950" />
            </label>
            <div className="flex gap-2">
              <button type="button" onClick={() => setShowFilters(value => !value)} className="inline-flex h-10 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 text-sm font-medium text-title-page-color-100 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:hover:bg-gray-700">
                <FiFilter /> Filters {activeFilterCount > 0 && <span className="rounded-full bg-blue-600 px-1.5 py-0.5 text-[11px] text-white">{activeFilterCount}</span>}
              </button>
              <Button type="submit">Search</Button>
            </div>
          </div>

          {showFilters && (
            <div className="mt-4 border-t border-gray-200 pt-4 dark:border-gray-700">
              <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
                <label className="text-sm font-medium text-title-page-color-100 dark:text-gray-200">Person or email
                  <input name="actor" defaultValue={searchParams.get('actor') || ''} placeholder="e.g. Maya Chen" className="mt-1.5 h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm font-normal outline-none focus:border-blue-500 dark:border-gray-600 dark:bg-gray-900" />
                </label>
                <label className="text-sm font-medium text-title-page-color-100 dark:text-gray-200">Activity
                  <select name="action" defaultValue={searchParams.get('action') || ''} className="mt-1.5 h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm font-normal outline-none focus:border-blue-500 dark:border-gray-600 dark:bg-gray-900">
                    <option value="">All activity</option>
                    {ACTION_GROUPS.map(([group, actions]) => (
                      <optgroup key={group} label={group}>
                        {actions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                      </optgroup>
                    ))}
                  </select>
                </label>
                <label className="text-sm font-medium text-title-page-color-100 dark:text-gray-200">Result
                  <select name="outcome" defaultValue={searchParams.get('outcome') || ''} className="mt-1.5 h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm font-normal outline-none focus:border-blue-500 dark:border-gray-600 dark:bg-gray-900">
                    <option value="">Any result</option>
                    {Object.entries(OUTCOME_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </label>
                <label className="text-sm font-medium text-title-page-color-100 dark:text-gray-200">Location
                  <input name="scope" defaultValue={searchParams.get('scope') || ''} placeholder="e.g. Aghbala" className="mt-1.5 h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm font-normal outline-none focus:border-blue-500 dark:border-gray-600 dark:bg-gray-900" />
                </label>
                <label className="text-sm font-medium text-title-page-color-100 dark:text-gray-200">From
                  <input name="from" type="date" defaultValue={searchParams.get('created_from')?.slice(0, 10) || ''} className="mt-1.5 h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm font-normal outline-none focus:border-blue-500 dark:border-gray-600 dark:bg-gray-900" />
                </label>
                <label className="text-sm font-medium text-title-page-color-100 dark:text-gray-200">To
                  <input name="to" type="date" defaultValue={searchParams.get('created_to')?.slice(0, 10) || ''} className="mt-1.5 h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm font-normal outline-none focus:border-blue-500 dark:border-gray-600 dark:bg-gray-900" />
                </label>
              </div>
              <div className="mt-4 flex items-center justify-end gap-2 border-t border-gray-100 pt-4 dark:border-gray-700/60">
                {activeFilterCount > 0 && (
                  <button type="button" onClick={() => { setCursors(['']); setSearchParams({}); }} className="inline-flex h-9 items-center gap-1.5 rounded-lg px-3 text-sm font-medium text-title-page-color-300 hover:text-title-page-color-100 dark:text-gray-400 dark:hover:text-white">
                    <FiX /> Reset
                  </button>
                )}
                <Button type="submit" size="sm">Apply filters</Button>
              </div>
            </div>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="mr-1 text-xs text-title-page-color-300 dark:text-gray-400">Quick view</span>
            <button type="button" onClick={() => setDateRange(1)} className="rounded-full border border-gray-200 px-3 py-1 text-xs font-medium text-title-page-color-300 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-blue-950/40">Today</button>
            <button type="button" onClick={() => setDateRange(7)} className="rounded-full border border-gray-200 px-3 py-1 text-xs font-medium text-title-page-color-300 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-blue-950/40">Last 7 days</button>
            <button type="button" onClick={() => updateFilters({ outcome: 'failed' })} className="rounded-full border border-gray-200 px-3 py-1 text-xs font-medium text-title-page-color-300 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-rose-950/40">Failed</button>
            <button type="button" onClick={() => updateFilters({ outcome: 'denied' })} className="rounded-full border border-gray-200 px-3 py-1 text-xs font-medium text-title-page-color-300 hover:border-orange-300 hover:bg-orange-50 hover:text-orange-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-orange-950/40">Blocked</button>
            {activeFilterCount > 0 && <button type="button" onClick={() => { setCursors(['']); setSearchParams({}); }} className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400"><FiX /> Clear all</button>}
          </div>
        </form>

        <div className={`mt-4 grid items-start gap-4 ${selected ? 'lg:grid-cols-[minmax(0,1fr)_24rem]' : 'grid-cols-1'}`}>
          <section className="min-w-0 overflow-hidden rounded-xl border border-gray-200 bg-card-color-100 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-center justify-between gap-4 border-b border-gray-200 px-4 py-3 dark:border-gray-700 sm:px-5">
              <div>
                <h2 className="text-sm font-semibold text-title-page-color-100 dark:text-white">Activity</h2>
                <p className="mt-0.5 text-xs text-title-page-color-300 dark:text-gray-400">Most recent first</p>
              </div>
              {!query.isLoading && !query.isError && events.length > 0 && (
                <span className="text-sm font-medium text-title-page-color-300 dark:text-gray-400">
                  {events.length} {events.length === 1 ? 'result' : 'results'}{cursors.length > 1 ? ` · page ${cursors.length}` : ''}
                </span>
              )}
            </div>

            {query.isError ? (
              <div className="flex flex-col items-center px-6 py-16 text-center">
                <FiAlertCircle className="h-8 w-8 text-rose-500" />
                <p className="mt-3 font-semibold text-title-page-color-100 dark:text-white">Activity could not be loaded</p>
                <p className="mt-1 text-sm text-title-page-color-300 dark:text-gray-400">Your filters are still here. Try loading the results again.</p>
                <Button onClick={() => query.refetch()} size="sm" className="mt-4">Try again</Button>
              </div>
            ) : query.isLoading ? (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {Array.from({ length: 7 }).map((_, index) => <div key={index} className="flex gap-4 px-5 py-4"><div className="h-9 w-9 animate-pulse rounded-full bg-gray-100 dark:bg-gray-700" /><div className="flex-1 space-y-2"><div className="h-3 w-1/3 animate-pulse rounded bg-gray-100 dark:bg-gray-700" /><div className="h-3 w-2/3 animate-pulse rounded bg-gray-100 dark:bg-gray-700" /></div></div>)}
              </div>
            ) : events.length === 0 ? (
              <div className="flex flex-col items-center px-6 py-16 text-center">
                <FiShield className="h-9 w-9 text-gray-300 dark:text-gray-600" />
                <p className="mt-3 font-semibold text-title-page-color-100 dark:text-white">No matching activity</p>
                <p className="mt-1 max-w-md text-sm text-title-page-color-300 dark:text-gray-400">Try a broader date range, remove a filter, or check back after people begin using the platform.</p>
                {activeFilterCount > 0 && <Button variant="outline" size="sm" onClick={() => setSearchParams({})} className="mt-4">Clear filters</Button>}
              </div>
            ) : (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full text-left">
                    <thead className="bg-card-color-200/70 text-xs text-title-page-color-300 dark:bg-gray-900/50 dark:text-gray-400"><tr>
                      <th className="px-5 py-3 font-semibold">When</th><th className="px-5 py-3 font-semibold">Person</th><th className="px-5 py-3 font-semibold">Activity</th><th className="px-5 py-3 font-semibold">Affected item</th><th className="px-5 py-3 font-semibold">Location</th><th className="px-5 py-3 font-semibold">Result</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {events.map(event => {
                        const actor = actorDetails(event);
                        const ActivityIcon = activityIcon(event.action);
                        const isSelected = selected?.id === event.id;
                        const rowTone = isSelected
                          ? 'bg-blue-50 dark:bg-blue-950/30'
                          : event.outcome === 'failed'
                            ? 'bg-rose-50/50 hover:bg-rose-50 dark:bg-rose-950/20 dark:hover:bg-rose-950/40'
                            : event.outcome === 'denied'
                              ? 'bg-orange-50/50 hover:bg-orange-50 dark:bg-orange-950/20 dark:hover:bg-orange-950/40'
                              : 'hover:bg-blue-50/60 dark:hover:bg-gray-700/50';
                        return <tr
                          key={event.id}
                          onClick={() => setSelected(event)}
                          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelected(event); } }}
                          tabIndex={0}
                          role="button"
                          aria-label={`${activityLabel(event)}, ${OUTCOME_LABELS[event.outcome]}, ${formatDate(event.created_at)}`}
                          aria-pressed={isSelected}
                          className={`cursor-pointer outline-none transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 ${rowTone}`}
                        >
                          <td className="whitespace-nowrap px-5 py-3.5"><p className="text-sm text-title-page-color-100 dark:text-gray-200">{formatDate(event.created_at)}</p><p className="mt-0.5 text-xs text-title-page-color-300 dark:text-gray-400">{formatTime(event.created_at)}</p></td>
                          <td className="px-5 py-3.5"><div className="flex min-w-[11rem] items-center gap-2.5"><span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-50 text-[11px] font-bold text-blue-700 dark:bg-blue-950/50 dark:text-blue-300">{actor.initials}</span><div className="min-w-0"><p className="truncate text-sm font-medium text-title-page-color-100 dark:text-white">{actor.primary}</p>{actor.secondary && <p className="truncate text-xs text-title-page-color-300 dark:text-gray-400">{actor.secondary}</p>}</div></div></td>
                          <td className="px-5 py-3.5"><div className="flex items-center gap-2.5"><span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-card-color-200 text-title-page-color-300 dark:bg-gray-700 dark:text-gray-300"><ActivityIcon className="h-3.5 w-3.5" /></span><span className="text-sm font-medium text-title-page-color-100 dark:text-gray-200">{activityLabel(event)}</span></div></td>
                          <td className="px-5 py-3.5 text-sm text-title-page-color-300 dark:text-gray-300">{targetLabel(event)}</td>
                          <td className="px-5 py-3.5"><span className="inline-flex items-center gap-1.5 text-sm text-title-page-color-300 dark:text-gray-300"><FiMapPin className="text-gray-400" /><ScopeLabel event={event} /></span></td>
                          <td className="px-5 py-3.5"><OutcomeBadge outcome={event.outcome} /></td>
                        </tr>;
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="divide-y divide-gray-100 md:hidden dark:divide-gray-700">
                  {events.map(event => {
                    const actor = actorDetails(event);
                    const ActivityIcon = activityIcon(event.action);
                    const cardTone = event.outcome === 'failed'
                      ? 'bg-rose-50/50 hover:bg-rose-50 dark:bg-rose-950/20'
                      : event.outcome === 'denied'
                        ? 'bg-orange-50/50 hover:bg-orange-50 dark:bg-orange-950/20'
                        : 'hover:bg-blue-50 dark:hover:bg-gray-700/50';
                    return <button key={event.id} type="button" onClick={() => setSelected(event)} className={`w-full px-4 py-4 text-left outline-none transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 ${cardTone}`}>
                      <div className="flex items-start justify-between gap-3"><div className="flex min-w-0 items-start gap-2.5"><span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-card-color-200 text-title-page-color-300 dark:bg-gray-700 dark:text-gray-300"><ActivityIcon className="h-3.5 w-3.5" /></span><div className="min-w-0"><p className="font-semibold text-title-page-color-100 dark:text-white">{activityLabel(event)}</p><p className="mt-1 truncate text-sm text-title-page-color-300 dark:text-gray-400">{actor.primary}{actor.secondary ? ` · ${actor.secondary}` : ''}</p></div></div><OutcomeBadge outcome={event.outcome} /></div>
                      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-title-page-color-300 dark:text-gray-400"><span className="inline-flex items-center gap-1"><FiCalendar />{formatDate(event.created_at)}</span><span className="inline-flex items-center gap-1"><FiClock />{formatTime(event.created_at)}</span><span className="inline-flex items-center gap-1"><FiMapPin /><ScopeLabel event={event} /></span></div>
                    </button>;
                  })}
                </div>
              </>
            )}

            {!query.isLoading && !query.isError && events.length > 0 && (
              <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 dark:border-gray-700 sm:flex-row sm:items-center sm:justify-between sm:px-5">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                  <label className="inline-flex items-center gap-2 text-xs font-medium text-title-page-color-300 dark:text-gray-400">
                    Rows per page
                    <select
                      value={pageSize}
                      onChange={event => changePageSize(Number(event.target.value))}
                      className="h-8 rounded-lg border border-gray-200 bg-white px-2 text-sm font-medium text-title-page-color-100 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:focus:ring-blue-950"
                    >
                      {PAGE_SIZE_OPTIONS.map(size => <option key={size} value={size}>{size}</option>)}
                    </select>
                  </label>
                  <span className="text-xs text-title-page-color-300 dark:text-gray-400">
                    {(!query.data?.next_cursor && cursors.length === 1)
                      ? `Showing all ${events.length} ${events.length === 1 ? 'result' : 'results'}`
                      : `${events.length} ${events.length === 1 ? 'result' : 'results'} · page ${cursors.length}`}
                  </span>
                </div>
                <div className="flex gap-2">
                  <button type="button" disabled={cursors.length === 1 || query.isFetching} onClick={() => setCursors(items => items.slice(0, -1))} className="inline-flex h-9 items-center gap-1 rounded-lg border border-gray-200 px-3 text-sm font-medium text-title-page-color-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"><FiChevronLeft /> Previous</button>
                  <button type="button" disabled={!query.data?.next_cursor || query.isFetching} onClick={() => setCursors(items => [...items, query.data!.next_cursor!])} className="inline-flex h-9 items-center gap-1 rounded-lg border border-gray-200 px-3 text-sm font-medium text-title-page-color-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700">Next <FiChevronRight /></button>
                </div>
              </div>
            )}
          </section>

          {selected && <Inspector event={selected} onClose={() => setSelected(null)} />}
        </div>
      </div>
    </div>
  );
}
